import classNames from "classnames";
import {Link} from "react-router";

import {Divider} from "@/components/divider/Divider.tsx";
import {StatusPill} from "@/components/statusPill/StatusPill.tsx";
import {PillStatus} from "@/components/statusPill/StatusPill.types.ts";
import {percentage} from "@/lib/format.tsx";
import {CheckTypeDisplay, Service, ServiceDefinition} from "@/lib/types/checks.ts";
import {DashboardDefinition, ServicePerformanceMetric} from "@/lib/types/website.ts";
import {getCheckDetailUrl, isEmptyGroupCheck} from "@/pages/checks/utils.ts";
import {CheckResponseTime} from "@/pages/dashboard/display/components/checkCard/CheckResponseTime.tsx";
import {ServiceState} from "@/pages/dashboard/display/components/checkCard/ServiceState.tsx";
import {uptimePercentageCSSClass} from "@/pages/dashboard/display/dashboardUtils.ts";

import styles from "../CheckCard.module.scss";

interface CheckCardProps {
  dashboardDef: DashboardDefinition;
  service: Service;
  performanceMetric: ServicePerformanceMetric;
  serviceDefinition: ServiceDefinition;
}

export const CheckCard = ({dashboardDef, service, performanceMetric, serviceDefinition}: CheckCardProps) => {
  const displayName = service.name ? service.name : service.device.address;
  const displayAddress = service.name || service.monitoring_service_type === "HTTP" ? service.msp_address : "";

  return (
    <div className="col">
      <Link
        data-testid="check-card"
        to={getCheckDetailUrl(service.id, service.monitoring_service_type, isEmptyGroupCheck(service))}
      >
        <div
          className={classNames(styles.checkCardTop, {
            [styles.checkCardTopUp]: service.cached_state_is_up && !service.is_paused,
            [styles.checkCardTopPaused]: service.is_paused,
            [styles.checkCardTopDown]: !service.cached_state_is_up && !service.is_paused,
          })}
        ></div>

        <div className={styles.checkCardBody}>
          <div className="row-gap-0 p-0">
            <div className="row">
              <div className="col display-block d-flex align-text-top">
                <div className="overflow-hidden w-100">
                  <div
                    data-testid="check-name"
                    title={displayName}
                    className={classNames("text-truncate mb-1", styles.checkCardName)}
                  >
                    {displayName}
                  </div>

                  <div title={displayAddress} className={classNames("text-truncate mb-0", styles.checkCardAddress)}>
                    {displayAddress}
                  </div>
                </div>
                {service.is_under_maintenance && (
                  <StatusPill type="checks" variant="icon" status={PillStatus.MAINTENANCE} />
                )}
              </div>
            </div>
            <Divider className={"my-3"} />
            <div className="row pb-3">
              <div className={styles.checkCardStatName}>{CheckTypeDisplay[service.monitoring_service_type]}</div>
              <ServiceState service={service} />
            </div>
            {dashboardDef.services_show_uptime && !service.is_paused && performanceMetric.uptime_24h !== null && (
              <div className="row pb-3">
                <div className={styles.checkCardStatName}>Uptime – Last 24h</div>
                <div
                  className={classNames(
                    styles.checkCardStatValue,
                    uptimePercentageCSSClass(performanceMetric.uptime_24h, Number(service.msp_uptime_sla))
                  )}
                >
                  {percentage(performanceMetric.uptime_24h, 4)}
                </div>
              </div>
            )}
            {dashboardDef.services_show_response_time && !service.is_paused && (
              <div className="row">
                <CheckResponseTime
                  service={service}
                  responseTimeData={performanceMetric.response_time_datapoints}
                  serviceDefinition={serviceDefinition}
                />
              </div>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
};
